/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import noop from 'lodash/noop';
import partition from 'lodash/partition';
import keyBy from 'lodash/keyBy';
import some from 'lodash/some';
import every from 'lodash/every';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import filter from 'lodash/filter';

import {
  errorAccountGrowth,
  errorConsolidatedPerformance,
  errorGiftIndicators,
  errorHistory,
  errorIndividualAccountGrowth,
  errorIntradayPricingManual,
  errorMailingAddress,
  errorProductPerformance,
  errorUpdateWorkspacePreferences,
  errorYTDBalanceData,
  errorWorkspacePreferences,
  receivedAccountGrowth,
  receivedIndividualAccountGrowth,
  receivedIntradayPricingManual,
  receivedConsolidatedPerformance,
  receivedGiftIndicators,
  receivedHistory,
  receivedMailingAddress,
  receivedProductPerformance,
  receivedUpdateWorkspacePreferences,
  receivedWorkspacePreferences,
  receivedYTDBalanceData,
  requestedAccountGrowth,
  requestedConsolidatedPerformance,
  requestedGiftIndicators,
  requestedHistory,
  requestedIndividualAccountGrowth,
  requestedIntradayPricingManual,
  requestedMailingAddress,
  requestedUpdateWorkspacePreferences,
  requestedWorkspacePreferences,
  requestedYTDBalanceData,
  requestedAggregatedProductPerformance,
  receivedAggregatedProductPerformance,
  errorAggregatedProductPerformance,
  requestedResidentialAddress,
  receivedResidentialAddress,
  errorResidentialAddress,
  requestedIntradayPricingAutomatic,
  receivedIntradayPricingAutomatic,
  requestedCDSPortfolio,
  receivedCDSPortfolio,
  errorCDSPortfolio,
  requestedUnrealizedGainsLosses,
  receivedUnrealizedGainsLosses,
  errorUnrealizedGainsLosses,
  updateIntradayAccountStatus,
  errorIntradayPricingAutomatic,
  requestedRealizedGainsLossesPrior,
  receivedRealizedGainsLossesPrior,
  errorRealizedGainsLossesPrior,
  requestedRealizedGainsLossesCurrent,
  receivedRealizedGainsLossesCurrent,
  errorRealizedGainsLossesCurrent,
} from 'state/Actions';
import {
  errorCCPADocumentReferences,
  errorDocumentDownload,
  errorDocumentReferences,
  receivedCCPADocumentReferences,
  receivedDocumentDownload,
  receivedDocumentReferences,
  requestedDocumentDownload,
  requestedDocumentReferences
} from 'state/statements/StatementsActions';
import {
  errorAccountHistory,
  receivedAccountHistory,
  requestedAccountHistory
} from 'state/account/IndividualAccountActions';

import {
  errorMetadata,
  receivedMetadata,
  requestedMetadata
} from 'state/api/MetadataActions';
import {
  errorCustomer,
  receivedCustomer,
  requestedCustomer
} from 'state/customer/CustomerActions';

import {
  errorCustomVideo,
  receivedCustomVideo,
  requestedCustomVideo
} from 'state/customVideo/CustomVideoActions';

import {
  errorAvailableFunds,
  errorCustomAccountGroups,
  errorCustomerPreferences,
  errorDividendsAndCapitalGains,
  errorDocumentDelivery,
  errorMutualFunds,
  errorSubscriptions,
  errorUpdateCustomerPreferences,
  errorUpdateDividendsAndCapitalGains,
  errorUpdateDocumentDelivery,
  hideFixedFooterSaveOptions,
  receivedAvailableFunds,
  receivedCustomAccountGroups,
  receivedCustomerPreferences,
  receivedDividendsAndCapitalGains,
  receivedDocumentDelivery,
  receivedMutualFunds,
  receivedSubscriptions,
  receivedUpdateCustomerPreferences,
  receivedUpdateDividendsAndCapitalGains,
  receivedUpdateDocumentDelivery,
  receivedCascadingSubscriptions,
  requestedAvailableFunds,
  requestedCustomAccountGroups,
  requestedCustomerPreferences,
  requestedDividendsAndCapitalGains,
  requestedDocumentDelivery,
  requestedMutualFunds,
  requestedSubscriptions,
  requestedUpdateCustomerPreferences,
  requestedUpdateDividendsAndCapitalGains,
  requestedUpdateDocumentDelivery,
  requestedCasCadingSubscriptions,
  sendDivCapBusinessEvent,
  receivedUpdateMutualFundDividendsAndCapitalGains
} from 'state/preferences/PreferencesActions';
import {
  errorUpdateSubscriptions,
  receivedUpdateSubscriptions,
  requestedUpdateSubscriptions,
  saveSubscriptionsError,
  saveSubscriptionsSuccess
} from 'state/subscriptions/SubscriptionsActions';
import {
  errorSsoAuthentication,
  errorServerPing,
  receivedSsoAuthentication,
  receivedServerPing,
  requestedSsoAuthentication
} from 'state/auth/AuthActions';
import {
  accountRestriction,
  errorAccounts,
  errorPurgedAccounts,
  receivedAccounts,
  receivedPurgedAccounts,
  requestedAccounts,
  requestedPurgedAccounts
} from 'state/account/AccountBalancesActions';
import {
  errorCancelPendingTransaction,
  errorHistoricalTransactions,
  errorPendingTransactions,
  errorPriorBusinessDay,
  receivedCancelPendingTransaction,
  receivedHistoricalTransactions,
  receivedHistoricalTransactionsV2,
  receivedPendingTransactions, receivedPendingTransactionsV2,
  receivedPriorBusinessDay,
  requestedCancelPendingTransaction,
  requestedHistoricalTransactions,
  requestedPendingTransactions,
  requestedPriorBusinessDate
} from 'state/transaction/TransactionActions';
import {
  errorTotalPerformance,
  receivedTotalPerformance,
  requestedTotalPerformance
} from 'state/performance/PerformanceActions';
import {
  errorAccountPerformance,
  receivedAccountPerformance,
  requestedAccountPerformance
} from 'state/account/AccountPerformanceActions';
import {
  errorMessages,
  errorMmrMessages,
  receivedMessages,
  receivedMmrMessages,
  requestedMessages,
  requestedMmrMessages
} from 'state/notification/NotificationActions';
import {
  requestedBeneficiaries,
  receivedBeneficiaries,
  errorBeneficiaries,
  requestedUpdateBeneficiaries,
  receivedUpdateBeneficiaries,
  errorUpdateBeneficiaries
} from 'state/beneficiaries/BeneficiaryActions';
import {
  errorContributionLimit,
  errorContributionPeriod,
  receivedContributionLimit,
  receivedContributionPeriod,
  requestedContributionLimit,
  requestedContributionPeriod
} from 'state/contributions/IRAContributionLimitActions';

import {
  errorHistoricalBalances,
  receivedHistoricalBalances,
  requestedHistoricalBalances
} from 'state/account/HistoricalBalanceActions';
import {
  errorRequiredMinimumDistribution,
  receivedRequiredMinimumDistribution,
  requestedRequiredMinimumDistribution
} from 'state/distribution/RequiredMinimumDistributionActions';
import {
  errorRequiredMinimumDistributionSummary,
  receivedRequiredMinimumDistributionSummary,
  requestedRequiredMinimumDistributionSummary
} from 'state/distribution/RequiredMinimumDistributionSummaryActions';
import {
  errorExternalInvestmentStatus,
  receivedExternalInvestmentStatus,
  requestedExternalInvestmentStatus,
  errorSetExternalInvestmentConsent,
  receivedSetExternalInvestmentConsent,
  requestedSetExternalInvestmentConsent,
  receivedExternalInvestmentConsent,
  updateExternalInvestmentStatus,
  errorExternalInvestmentConsent
} from 'state/external/ExternalInvestmentConsentActions';
import {
  errorExternalInvestmentBalances,
  receivedExternalInvestmentBalances,
  requestedExternalInvestmentBalances,
  requestedRefreshExternalInvestmentBalances,
  receivedRefreshExternalInvestmentBalances,
  errorRefreshExternalInvestmentBalances
} from 'state/external/ExternalInvestmentBalancesActions';
import {
  errorIRTMessages,
  receivedIRTMessages,
  requestedIRTMessages,
  suppressedIRTMessage
} from 'state/IRT/IRTMessagesActions';
import {
  errorCMMUpdate,
  errorCMMVerification,
  receivedCMMUpdate,
  receivedCMMUpdateStopMail,
  errorCMMUpdateStopMail,
  receivedCMMVerification,
  requestedCMMUpdate,
  requestedCMMVerification
} from 'state/contactMethodMaintenance/ContactMethodMaintenanceActions';
import {
  errorConfidenceScores,
  receivedConfidenceScores,
  requestedConfidenceScores,
  requestedMonteCarloConfidenceScores,
  receivedMonteCarloConfidenceScores,
  errorMonteCarloConfidenceScores
} from 'state/account/ConfidenceScoresActions';

import {
  errorNASUApplications,
  receivedNASUApplications,
  errorNASUDeleteApplications,
  receivedNASUDeleteApplications
} from 'state/nasuApplications/nasuApplicationsActions';
import {
  errorTaxes,
  receivedTaxes,
  requestedTaxes
} from 'state/taxes/TaxActions';
import {
  errorCommentary,
  receivedCommentary,
  requestedCommentary
} from 'state/commentary/CommentaryActions';
import { getCMMEmailAddress } from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import Store from 'state/Store';
import { CCPA_DOCUMENT_ID } from 'state/statements/StatementsSelectors';
import {
  documentAccountShellEnabled,
  newMessageCenterCountEnabled,
  requestCacheEnabled,
  isRMDDirectEnabled,
  residentialAddressEnabled,
  clientMissionsEnabled,
} from 'services/FeatureFlags';
import { yyyymmdd } from 'services/Dates';
import {
  messageServiceBasePath,
  withTrailingSlash,
  NET_X_LOGOUT_URL,
  NET_X_MESSAGE_URL
} from 'services/Externals';
import {
  arrayOfAccountDescriptors,
  getAccountDescriptor
} from 'services/Accounts';
import { formatAccountType } from 'services/Formats';
import {
  isBrokerage,
  isActivePlus,
  isActiveAdvisory,
  isCollegeSavings,
  isWorkplaceRetirement,
  BROKERAGE
} from 'services/Products';
import { DEFAULT_TRANSACTIONS_DAYS } from 'services/Transactions';
import {
  deleteGrantViewAcessError,
  errorGrantViewAccessAddUserAuth,
  errorUpdateViewRelationships,
  errorViewRelationships,
  errorAddViewRelationships,
  receivedDeleteGrantViewAccessUser,
  receivedGrantViewAccessAddUserAuth,
  receivedUpdateViewRelationships,
  requestedAddViewRelationships,
  receivedViewRelationships,
  receivedAddViewRelationships,
  requestDeleteGrantViewAccessUser,
  requestedGrantViewAccessAddUserAuth,
  requestedUpdateViewRelationships,
  requestedViewRelationships
} from 'state/grantViewAccess/GrantViewAccessActions';
import {
  errorAssetAllocation,
  receivedAssetAllocation,
  requestedAssetAllocation
} from 'state/assetAllocation/AssetAllocationActions';
import {
  errorBankProfile,
  receivedBankProfile,
  requestedBankProfile,
  requestedUpdateBankAssociations,
  receivedUpdateBankAssociations,
  errorUpdateBankAssociations,
  requestedDeleteBank,
  receivedDeleteBank,
  errorDeleteBank,
  requestedChallengeDepositVerification,
  receivedChallengeDepositVerification,
  errorChallengeDepositVerification,
  requestedEligibleAgreementsForBank,
  receivedEligibleAgreementsForBank,
  errorEligibleAgreementsForBank,
  receivedReinitiateChallengeDeposits,
  errorReinitiateChallengeDeposits,
  requestedReinitiateChallengeDeposits
} from 'state/linkedBankAccounts/LinkedBankAccountsActions';

import {
  receivedAlertsAndNotifications,
  requestedAlertsAndNotifications,
  errorAlertsAndNotifications,
  requestedUpdateAlertsAndNotifications,
  receivedUpdateAlertsAndNotifications,
  errorUpdateAlertsAndNotifications,
  requestedActivatedForTextAlerts,
  receivedActivatedForTextAlerts,
  errorActivatedForTextAlerts,
  requestedVerifySmsActivationCode,
  receivedVerifySmsActivationCode,
  errorVerifySmsActivationCode,
  requestedSubmitSmsActivation,
  receivedSubmitSmsActivation,
  errorSubmitSmsActivation,
  requestedSecurityOperationalConsent,
  receivedSecurityOperationalConsent,
  errorSecurityOperationalConsent,
  requestedSendSmsActivationCode,
  receivedSendSmsActivationCode,
  errorSendSmsActivationCode,
  requestedMobileCarriers,
  receivedMobileCarriers,
  errorMobileCarriers,
  requestedShortCodeDeactivationReason,
  receivedShortCodeDeactivationReason,
  errorShortCodeDeactivationReason
} from 'state/alertsAndNotifications/AlertsAndNotificationsActions';

import { getENV, GLOBALS, NODE_ENVIRONMENTS } from 'services/globals';

import {
  errorHouseholdInformation,
  receivedHouseholdInformation,
  requestedHouseholdInformation
} from 'state/household/HouseholdInformationActions';
import {
  requestedKiplingerRegistrationInfo,
  receivedKiplingerRegistrationInfo,
  errorKiplingerRegistrationInfo
} from 'state/kiplingerRegistration/KiplingerRegistrationActions';
import {
  errorRpsSubscriptionInformation,
  receivedRpsSubscriptionInformation,
  requestedRpsSubscriptionInformation,
  requestedUnlinkRpsSubscription,
  recievedUpdateRpsSubscription,
  errorUnlinkRpsSubscription,
  requestedLinkRpsSubscription,
  errorLinkRpsSubscription
} from 'state/LinkRpsAccounts/LinkRpsAccountsActions';
import {
  requestedMorningstarAssociation,
  receivedMorningstarAssociation,
  errorMorningstarAssociation,
  requestedMorningstarAssociationUrl,
  receivedMorningstarAssociationUrl,
  errorMorningstarAssociationUrl,
  requestedMorningstarConsent,
  receivedMorningstarConsent,
  errorMorningstarConsent,
  requestedMorningstarRegistrationCallback,
  receivedMorningstarRegistrationCallback,
  errorMorningstarRegistrationCallback
} from 'state/morningstar/MorningstarActions';
import { isSalesforceRepEnabled } from 'state/view/ViewSelectors';

import {
  requestedWorkplaceYearEndBalanceData,
  receivedWorkplaceYearEndBalanceData,
  errorWorkplaceYearEndBalanceData
} from 'state/account/RpsAccountBalancesActions';
import { isCommentaryLoaded } from 'state/commentary/CommentarySelectors';
import { BROKERAGE_CONFIRMATION } from 'services/Statements';
import { commentaryMap } from './Commentary';
import {
  analyticsActionError,
  analyticsActionSuccess
} from 'state/analytics/AnalyticsActions';
import { hasCustomError } from './CustomExceptions';
import { getApiKey } from '../state/config/ConfigSelectors';
import {
  errorPortfolio,
  receivedPortfolio,
  requestedPortfolio
} from 'state/portfolio/PortfolioActions';
import { nasuInprogressReturnJourneyTile } from './FeatureFlags';
import {
  errorMissions,
  errorUpdatingMission,
  receivedMissions,
  receivedUpdateMission,
  requestedMissions,
  requestedUpdateMission
} from 'state/missions/MissionActions';
import {
  receivedConversionHistory,
  errorConversionHistory,
  requestedConversionHistory
} from 'state/conversionHistory/ConversionHistoryActions';
import {
  errorBucketRoll,
  receivedBucketRoll,
  requestedBucketRoll
} from 'state/bucketRoll/BucketRollActions';
import {
  errorRetrievingBrokerageAssertion,
  errorRetrievingBrokerageMFE,
  receivedBrokerageAssertion,
  receivedBrokerageMFE,
  requestedBrokerageAssertion,
  requestedBrokerageAssertionMFE,
  resetRetrievingBrokerageMFE,
  requestedFixedIncomeAssertion,
  receivedFixedIncomeAssertion,
  errorRetrievingFixedIncomeAssertion,
  updateBrokerageMFEState
} from "state/brokerage/BrokerageActions";
import { sendNASUDeleteAnalytics } from 'support/Analytics';
import { hasIntradayFail } from './React-Utils';
import {MFE_STATE} from "../support/BrokerageMFEUtils";

const appContext =
  getENV() === NODE_ENVIRONMENTS.LOCAL
    ? withTrailingSlash(GLOBALS.AAM_UI_TRP_APP_CONTEXT)
    : withTrailingSlash(GLOBALS.AAM_BFF_BASE_URL);

const BYPASS_CACHE = {};

const headerOptions = () => {
  const state = Store.getState();
  const isSalesforceRep = isSalesforceRepEnabled(state);

  const headers = {
    apikey: getApiKey(state),
    'X-TRP-REQUESTED-WITH': 'my-accounts',
    'Content-Type': 'application/json',
    'trp-request-consumer-initial': 'ii-account-access-service'
  };

  if (window.location.host && window.location.host.includes('awstrp.net')) {
    headers['import-oauth'] = true;
  }

  if (isSalesforceRep) {
    headers['trp-request-consumer-initial'] = 'ii-salesforce-aam';
  }

  return headers;
};

export const REST_OPTIONS = {
  DELETE: 'deleteOptions',
  DELETE_WITH_BODY: 'deleteOptionsWithBody',
  GET: 'getOptions',
  POST: 'postOptions',
  POST_WITH_BODY: 'postOptionsWithBody',
  PUT: 'putOptions',
  PUT_WITH_BODY: 'putOptionsWithBody'
};

export const getOptions = (customHeader) => {
  const headers = {
    ...headerOptions(),
    ...customHeader
  };
  return {
    method: 'GET',
    headers,
    credentials: 'include'
  };
};

export const postOptions = (customHeader) => {
  const headers = {
    ...headerOptions(),
    ...customHeader
  };
  return {
    method: 'POST',
    headers,
    credentials: 'include'
  };
};

export const deleteOptions = () => {
  return {
    method: 'DELETE',
    headers: headerOptions(),
    credentials: 'include'
  };
};

export const putOptions = (customHeader) => {
  const headers = {
    ...headerOptions(),
    ...customHeader
  };
  return {
    method: 'PUT',
    headers,
    credentials: 'include'
  };
};

export const deleteOptionsWithBody = (body) => {
  let newDeleteOptions = deleteOptions();
  newDeleteOptions.body = JSON.stringify(body);
  return newDeleteOptions;
};

export const putOptionsWithBody = (body, customHeader) => {
  let newPutOptions = putOptions(customHeader);
  newPutOptions.body = JSON.stringify(body);
  return newPutOptions;
};

export const postOptionsWithBody = (body, customHeader) => {
  let newPostOptions = postOptions(customHeader);
  newPostOptions.body = JSON.stringify(body);
  return newPostOptions;
};

export const appendHeader = (options, name, value) => {
  const updatedOptions = options;
  const { headers } = updatedOptions;
  const updatedHeaders = {
    ...headers,
    [name]: value
  };
  updatedOptions.headers = updatedHeaders;
  return updatedOptions;
};

export function echoOrchis() {
  return aamFetch(
    'https://api.dev.awstrp.net/architecture-reference-implementations_echo-oauth-token/echo',
    getOptions()
  );
}

export function getExternalInvestmentSource(iframeUrl) {
  return aamFetch(iframeUrl, getOptions());
}

export const pershingLogout = () => {
  fetch(
    `${GLOBALS.AAM_UI_PERSHING_REST}/qa-netxinvestor-rest-service-xat-appservices/netxclient.rest-service/session/logout`,
    getOptions()
  ).then((response) => {
    if (response.status === 200 || response.status === 204) {
      // logout success
    }
  });
};

export const pershingSSOLogout = () => {
  fetch(
    `${GLOBALS.AAM_UI_PERSHING_REST}/nxi/sso-logout?message=logout&errorCode=null&null`,
    getOptions()
  ).then((response) => {
    if (response.status === 200 || response.status === 204) {
      // logout successful
    }
  });
};

export const getPrimaryData = (isInternal) => {
  const primaryDependencies = [
    pingServer(),
    getMetadata(),
    getCustomer(),
    getExternalInvestmentConsent(),
    getWorkspacePreferences(),
    getAccountBalances(isInternal),
    getPortfolio(),
    getIRTMessages(),
    getMessageNotifications()
  ];
  if (residentialAddressEnabled()) {
    primaryDependencies.push(getContactMethodMaintenanceV2());
  } else {
    primaryDependencies.push(getContactMethodMaintenance());
  }
  return Promise.all(primaryDependencies);
};

/*
TODO: Check with Allan if still needed
export const registerLogoutService = apiKey => {
  const registrationDetails = {
    app: 'accountaccess',
    type: 'fetch',
    url: 'https://troweprice.com/usis/my-accounts/'
  };

  aamFetch(`${GLOBALS.AAM_TRP_BASE_APIGEE_URL}/common-login-public/login-service/callbacks/register`, postOptionsWithBody(registrationDetails))
    .then(noop)
    .catch(noop);
};
*/

export const logPerformanceMetric = (event) => {
  return aamFetch('api/log/pageaction', postOptionsWithBody(event))
    .then(noop)
    .catch(noop);
};

export const registerLogoutCallback = (callbackDetails) => {
  aamFetch(
    `${GLOBALS.AAM_TRP_BASE_APIGEE_URL}/common-login-public/service/auth/logout/callbacks`,
    postOptionsWithBody(callbackDetails)
  )
    .then(noop)
    .catch(noop);
};

export const getMetadata = () => {
  Store.dispatch(requestedMetadata());
  aamFetch('api/metadata', getOptions())
    .then((metadata) => Store.dispatch(receivedMetadata(metadata)))
    .catch((error) => Store.dispatch(errorMetadata(error)));
};

export const getExternalInvestmentStatus = (isAdviceFlag) => {
  Store.dispatch(requestedExternalInvestmentStatus());
  const payload = {
    isAdvice: isAdviceFlag
  };
  return aamFetch(
    'api/accounts/external-investment/status',
    postOptionsWithBody(payload),
    BYPASS_CACHE
  )
    .then((response) =>
      Store.dispatch(receivedExternalInvestmentStatus(response))
    )
    .catch((error) => Store.dispatch(errorExternalInvestmentStatus(error)));
};

export const getExternalInvestmentConsent = () => {
  return aamFetch('api/accounts/external/consent', getOptions(), BYPASS_CACHE)
    .then((consent) =>
      Store.dispatch(receivedExternalInvestmentConsent(consent))
    )
    .catch((error) => Store.dispatch(errorExternalInvestmentConsent(error)));
};

export const getMutualFundInformation = () => {
  Store.dispatch(requestedMutualFunds());
  aamFetch('api/mutualfunds', getOptions())
    .then((funds) => Store.dispatch(receivedMutualFunds(funds)))
    .catch((error) => Store.dispatch(errorMutualFunds(error)));
};

export const getCustomer = () => {
  Store.dispatch(requestedCustomer());
  return aamFetch('api/customer/loggedin', getOptions())
    .then((customer) => {
      Store.dispatch(receivedCustomer(customer));
      const { webViewEnabled } = customer;
      getHouseholdInformation(webViewEnabled);
    })
    .catch((error) => Store.dispatch(errorCustomer(error)));
};

export const getCustomVideo = (
  productType,
  touchPoint,
  selectedBeneficiaryUber = ''
) => {
  Store.dispatch(requestedCustomVideo());
  const body = {
    productType,
    touchPoint,
    selectedBeneficiaryUber
  };
  return aamFetch(
    'api/customer/custom-video',
    postOptionsWithBody(body),
    BYPASS_CACHE
  )
    .then((customer) => Store.dispatch(receivedCustomVideo(customer)))
    .catch((error) => Store.dispatch(errorCustomVideo(error)));
};

export const getWorkspacePreferences = () => {
  Store.dispatch(requestedWorkspacePreferences());
  aamFetch('api/customer/workspace-preferences', getOptions())
    .then((workspacePreferences) =>
      Store.dispatch(receivedWorkspacePreferences(workspacePreferences))
    )
    .catch((error) => Store.dispatch(errorWorkspacePreferences(error)));
};

export const updateWorkspacePreferences = (payload) => {
  Store.dispatch(requestedUpdateWorkspacePreferences());
  aamFetch('api/customer/workspace-preferences', postOptionsWithBody(payload))
    .then((workspacePreferences) =>
      Store.dispatch(receivedUpdateWorkspacePreferences(workspacePreferences))
    )
    .catch((error) => Store.dispatch(errorUpdateWorkspacePreferences(error)));
};

export const getCustomerMailingAddress = () => {
  Store.dispatch(requestedMailingAddress());
  aamFetch('api/customer/mailing-address', getOptions())
    .then((mailingAddress) =>
      Store.dispatch(receivedMailingAddress(mailingAddress))
    )
    .catch((error) => Store.dispatch(errorMailingAddress(error)));
};

export const getCustomerResidentialAddress = () => {
  Store.dispatch(requestedResidentialAddress());
  aamFetch('api/customer/residential-address', getOptions())
    .then((residentialAddress) =>
      Store.dispatch(receivedResidentialAddress(residentialAddress))
    )
    .catch((error) => Store.dispatch(errorResidentialAddress(error)));
};

export const getCustomerPreferences = () => {
  Store.dispatch(requestedCustomerPreferences());
  aamFetch('api/customer/preferences', getOptions())
    .then((preferences) =>
      Store.dispatch(receivedCustomerPreferences(preferences))
    )
    .catch((error) => Store.dispatch(errorCustomerPreferences(error)));
};

export const getCustomerPreferencesV2 = () => {
  Store.dispatch(requestedCustomerPreferences());
  aamFetch('api/customer/preferences-v2', getOptions())
    .then((preferences) =>
      Store.dispatch(receivedCustomerPreferences(preferences))
    )
    .catch((error) => Store.dispatch(errorCustomerPreferences(error)));
};

//TODO: update with typeId
export const getRpsSubscriptionInformation = () => {
  Store.dispatch(requestedRpsSubscriptionInformation());
  return aamFetch('api/customer/service-type/status/111', getOptions())
    .then((response) =>
      Store.dispatch(receivedRpsSubscriptionInformation(response))
    )
    .catch((error) => Store.dispatch(errorRpsSubscriptionInformation(error)));
};

export const unlinkRpsSubscription = () => {
  Store.dispatch(requestedUnlinkRpsSubscription());
  return aamFetch(
    'api/customer/service-type/unsubscribe/111',
    postOptions(),
    BYPASS_CACHE
  )
    .then((response) => {
      if (!response) {
        const payload = {
          subscribed: false
        };
        Store.dispatch(recievedUpdateRpsSubscription(payload));
      }
    })
    .catch((error) => Store.dispatch(errorUnlinkRpsSubscription(error)));
};

export const linkRpsSubscription = () => {
  Store.dispatch(requestedLinkRpsSubscription());
  return aamFetch(
    'api/customer/service-type/subscribe/111',
    postOptions(),
    BYPASS_CACHE
  )
    .then((response) => {
      if (!response) {
        const payload = {
          subscribed: true
        };
        Store.dispatch(recievedUpdateRpsSubscription(payload));
      }
    })
    .catch((error) => Store.dispatch(errorLinkRpsSubscription(error)));
};

export const getAgreementBeneficiaries = () => {
  Store.dispatch(requestedBeneficiaries());
  aamFetch('api/accounts/beneficiaries', getOptions(), BYPASS_CACHE)
    .then((beneficiaries) =>
      Store.dispatch(receivedBeneficiaries(beneficiaries))
    )
    .catch((error) => Store.dispatch(errorBeneficiaries(error)));
};

export const addBeneficiaries = (beneficiaries, callback = () => {}, errorCallback) => {
  Store.dispatch(requestedUpdateBeneficiaries());
  aamFetch(
    'api/accounts/beneficiaries',
    postOptionsWithBody(beneficiaries),
    BYPASS_CACHE
  )
    .then((benes) => {
      Store.dispatch(receivedUpdateBeneficiaries(benes, "add", beneficiaries));
      callback();
      if (clientMissionsEnabled()) {
        try {
          getClientMissions();
        } catch (e) {
          // do nothing
        }
      }
    })
    .catch((error) => {
      Store.dispatch(errorUpdateBeneficiaries(error, beneficiaries));
      if (errorCallback) {
        errorCallback();
      }
      throw error;
    });
};

export const deleteBeneficiaries = (beneficiaries, callback = () => {}, errorCallback) => {
  Store.dispatch(requestedUpdateBeneficiaries());
  aamFetch(
    'api/accounts/beneficiaries',
    deleteOptionsWithBody(beneficiaries),
    BYPASS_CACHE
  )
    .then((benes) => {
      Store.dispatch(receivedUpdateBeneficiaries(benes, "delete", beneficiaries));
      callback();
      if (clientMissionsEnabled()) {
        try {
          getClientMissions();
        } catch (e) {
          // do nothing
        }
      }
    })
    .catch((error) => {
      Store.dispatch(errorUpdateBeneficiaries(error, beneficiaries));
      if (errorCallback) {
        errorCallback();
      }
      throw error;
    });
};

export const updateBeneficiaries = (beneficiaries, callback = () => {}, errorCallback) => {
  Store.dispatch(requestedUpdateBeneficiaries());
  aamFetch(
    'api/accounts/beneficiaries',
    putOptionsWithBody(beneficiaries),
    BYPASS_CACHE
  )
    .then((benes) => {
      Store.dispatch(receivedUpdateBeneficiaries(benes, "update", beneficiaries));
      callback();
      if (clientMissionsEnabled()) {
        try {
          getClientMissions();
        } catch (e) {
          // do nothing
        }
      }
    })
    .catch((error) => {
      Store.dispatch(errorUpdateBeneficiaries(error, beneficiaries));
      if (errorCallback) {
        errorCallback();
      }
      throw error;
    });
};

export const getPreferencesDividendsAndCapitalGains = () => {
  Store.dispatch(requestedDividendsAndCapitalGains());
  aamFetch('api/customer/preferences/dividendscapitalgains', getOptions())
    .then((dividendsCapitalGains) =>
      Store.dispatch(receivedDividendsAndCapitalGains(dividendsCapitalGains))
    )
    .catch((error) => Store.dispatch(errorDividendsAndCapitalGains(error)));
};

export const updatePreferencesDividendsAndCapitalGains = (payload) => {
  const productType =
    payload.distributionOptions[0]?.sourceAccountDescriptor.productType || 20;
  Store.dispatch(requestedUpdateDividendsAndCapitalGains());
  aamFetch(
    'api/customer/preferences/dividendscapitalgains',
    postOptionsWithBody(payload),
    BYPASS_CACHE
  )
    .then((dividendsCapitalGains) => {
      Store.dispatch(receivedUpdateDividendsAndCapitalGains(dividendsCapitalGains));
      if (productType === BROKERAGE) {
        Store.dispatch(updateIntradayAccountStatus(dividendsCapitalGains));
      } else {
        Store.dispatch(
          receivedUpdateMutualFundDividendsAndCapitalGains(dividendsCapitalGains)
        );
      }
      const eventPayload = {
        dividendsCapitalGains: payload
      };
      Store.dispatch(sendDivCapBusinessEvent(eventPayload));
    })
    .catch((error) =>
      Store.dispatch(errorUpdateDividendsAndCapitalGains(error))
    );
};

export const updateCustomerPreferences = (
  preferences,
  callBack = () => {},
  errorCallback
) => {
  const body = {
    ...preferences
  };
  Store.dispatch(requestedUpdateCustomerPreferences());
  aamFetch('api/customer/preferences', postOptionsWithBody(body), BYPASS_CACHE)
    .then((updatedPreferences) => {
      Store.dispatch(receivedUpdateCustomerPreferences(updatedPreferences));
      if (callBack) {
        callBack();
      }
    })
    .catch((error) => {
      Store.dispatch(errorUpdateCustomerPreferences(error));

      if (errorCallback) {
        errorCallback();
      }
      throw error;
    });
};

export const updateBrokerageCostBasis = (
  preferences,
  callBack = () => {},
  errorCallback = () => {}
) => {
  const body = {
    ...preferences
  };
  Store.dispatch(requestedUpdateCustomerPreferences());
  aamFetch(
    'api/customer/preferences/update/brokerage-cost-basis-election',
    putOptionsWithBody(body),
    BYPASS_CACHE
  )
    .then((updatedPreferences) => {
      Store.dispatch(
        receivedUpdateCustomerPreferences({
          ...updatedPreferences,
          brokerageTransaction: true
        })
      );
      if (callBack) {
        callBack();
      }
    })
    .catch((error) => {
      Store.dispatch(errorUpdateCustomerPreferences(error));
      if (errorCallback) {
        errorCallback();
      }
      throw error;
    });
};

export const updateCustomerPreferencesV2 = (
  preferences,
  callBack = () => {},
  errorCallback,
  costBasisElectionV2 = false
) => {
  const body = {
    ...preferences
  };
  if (!costBasisElectionV2) {
    Store.dispatch(requestedUpdateCustomerPreferences());
  }
  aamFetch(
    'api/customer/preferences-v2',
    postOptionsWithBody(body),
    BYPASS_CACHE
  )
    .then((updatedPreferences) => {
      if (!costBasisElectionV2) {
        Store.dispatch(receivedUpdateCustomerPreferences(updatedPreferences));
      }
      if (callBack) {
        callBack();
      }
    })
    .catch((error) => {
      if (!costBasisElectionV2) {
        Store.dispatch(errorUpdateCustomerPreferences(error));
      }
      if (errorCallback) {
        errorCallback();
      }
      throw error;
    });
};

export const getPreferencesViewRelationships = () => {
  Store.dispatch(requestedViewRelationships());
  aamFetch('api/view-access/relationships', getOptions(), BYPASS_CACHE)
    .then((relationships) =>
      Store.dispatch(receivedViewRelationships(relationships))
    )
    .catch((error) => Store.dispatch(errorViewRelationships(error)));
};

export const updatePreferencesViewRelationships = (
  payload,
  callback,
  isAdd = false
) => {
  if (isAdd) {
    Store.dispatch(requestedAddViewRelationships());
  } else {
    Store.dispatch(requestedUpdateViewRelationships());
  }
  return aamFetch(
    'api/view-access/relationships',
    postOptionsWithBody(payload),
    BYPASS_CACHE
  )
    .then(() => {
      if (isAdd) {
        Store.dispatch(receivedAddViewRelationships(payload));
      } else {
        Store.dispatch(receivedUpdateViewRelationships(payload));
      }
      callback();
    })
    .catch((error) => {
      if (isAdd) {
        Store.dispatch(errorAddViewRelationships(error));
      } else {
        Store.dispatch(errorUpdateViewRelationships(error));
      }
    });
};

export const getAssetAllocation = (accounts) => {
  Store.dispatch(requestedAssetAllocation());
  aamFetch('api/accounts/asset-allocation', postOptionsWithBody(accounts))
    .then((assetAllocation) =>
      Store.dispatch(receivedAssetAllocation(assetAllocation))
    )
    .catch((error) => Store.dispatch(errorAssetAllocation(error)));
};

export const getCustomerDocumentDelivery = () => {
  Store.dispatch(requestedDocumentDelivery());
  aamFetch(
    'api/customer/preferences/documentdelivery',
    getOptions(),
    BYPASS_CACHE
  )
    .then((documentDelivery) =>
      Store.dispatch(receivedDocumentDelivery(documentDelivery))
    )
    .catch((error) => Store.dispatch(errorDocumentDelivery(error)));
};

export const getCustomerSubscriptions = () => {
  Store.dispatch(requestedSubscriptions());
  aamFetch('api/customer/preferences/subscriptions', getOptions(), BYPASS_CACHE)
    .then((subscriptions) =>
      Store.dispatch(receivedSubscriptions(subscriptions))
    )
    .catch((error) => Store.dispatch(errorSubscriptions(error)));
};

/**
 * Update subscriptions supports Document Delivery
 */
export const updateDocumentDelivery = (
  payload,
  emailAddress,
  isCascadingUpdate
) => {
  const body = {
    ...payload,
    emailAddress
  };

  if (isCascadingUpdate) {
    Store.dispatch(requestedCasCadingSubscriptions());
  } else {
    Store.dispatch(requestedUpdateDocumentDelivery());
  }
  return aamFetch(
    'api/customer/preferences/subscriptions',
    postOptionsWithBody(body),
    BYPASS_CACHE
  )
    .then(({ result, message }) => {
      if (result) {
        Store.dispatch(hideFixedFooterSaveOptions());
        isCascadingUpdate
          ? Store.dispatch(
              receivedCascadingSubscriptions(payload.subscriptions, result)
            )
          : Store.dispatch(
              receivedUpdateDocumentDelivery(payload.subscriptions, result)
            );
      } else {
        throw new Error(message);
      }
    })
    .catch((error) => Store.dispatch(errorUpdateDocumentDelivery(error)));
};

/* This method supports subscriptions.js */
export const saveSubscriptions = (payload, updatedSubscriptions) => {
  Store.dispatch(requestedUpdateSubscriptions());
  return aamFetch(
    'api/customer/preferences/subscriptions',
    postOptionsWithBody(payload),
    BYPASS_CACHE
  )
    .then(({ result, message }) => {
      if (result) {
        Store.dispatch(hideFixedFooterSaveOptions());
        Store.dispatch(saveSubscriptionsSuccess(result));
        Store.dispatch(receivedUpdateSubscriptions(updatedSubscriptions));
      } else {
        throw new Error(message);
      }
    })
    .catch((error) => {
      Store.dispatch(saveSubscriptionsError(error));
      Store.dispatch(errorUpdateSubscriptions());
    });
};

export const updatePreferencesComplianceSubscriptions = (subscriptions) => {
  aamFetch(
    'api/customer/preferences/compliancesubscriptions',
    postOptionsWithBody(subscriptions)
  )
    .then(noop)
    .catch(noop);
};

export const validateAddresses = (addresses) => {
  const actions = addresses.map(validateAddress);
  return Promise.all(actions);
};

export const validateAddress = (address) => {
  const body = {
    ...address
  };
  return aamFetch(
    'api/customer/preferences/mailingaddress/verify',
    postOptionsWithBody(body),
    BYPASS_CACHE
  )
    .then((response) => {
      return {
        ...response,
        typeId: address.typeId
      };
    })
    .catch((error) => error);
};

export const sendBusinessEvent = (businessEvent) => {
  aamFetch(
    'api/customer/preferences/businessevent',
    postOptionsWithBody(businessEvent)
  )
    .then(() => noop())
    .catch(() => noop());
};

export const captureGVABusinessEvent = (businessEvent) => {
  aamFetch(
    'api/business-event/capture/grant-view-access',
    postOptionsWithBody(businessEvent),
    BYPASS_CACHE
  )
    .then(() => noop())
    .catch(() => noop());
};

export const captureBeneficiaryBusinessEvent = (businessEvent) => {
  aamFetch(
    'api/business-event/capture/beneficiaries',
    postOptionsWithBody(businessEvent)
  )
    .then(() => noop())
    .catch(() => noop());
};

export const captureCancelPendingTransactionBusinessEvent = (businessEvent) => {
  aamFetch(
    'api/business-event/capture/cancel-pending-transaction',
    postOptionsWithBody(businessEvent)
  )
    .then(() => noop())
    .catch(() => noop());
};

export const captureReinitiateBusinessEvent = (businessEvent) => {
  aamFetch(
    'api/business-event/capture/reinitiate',
    postOptionsWithBody(businessEvent),
    BYPASS_CACHE
  )
    .then(() => noop())
    .catch(() => noop());
};

export const captureBankingBusinessEvent = (businessEvent) => {
  aamFetch(
    'api/business-event/capture/bank',
    postOptionsWithBody(businessEvent),
    BYPASS_CACHE
  )
    .then(() => noop())
    .catch(() => noop());
};

export const captureContactInfoBusinessEvent = (businessEvent) => {
  aamFetch(
    'api/business-event/capture/contact-info',
    postOptionsWithBody(businessEvent),
    BYPASS_CACHE
  )
    .then(() => noop())
    .catch(() => noop());
};

export const captureEmailAddressBusinessEvent = (businessEvent) => {
  aamFetch(
    'api/business-event/capture/email-address',
    postOptionsWithBody(businessEvent),
    BYPASS_CACHE
  )
    .then(() => noop())
    .catch(() => noop());
};

export const captureDivCapBusinessEvent = (businessEvent) => {
  aamFetch(
    'api/business-event/capture/dividends-capital-gains',
    postOptionsWithBody(businessEvent),
    BYPASS_CACHE
  )
    .then(() => noop())
    .catch(() => noop());
};

export const getPurgedAccounts = () => {
  return aamFetch('api/accounts/purged', getOptions());
};

export const getAccounts = (purgedAccounts, isInternal) => {
  return aamFetch('api/accounts', getOptions())
    .then((accounts) => {
      //Format account type names for display purposes
      const allAccounts = accounts.concat(purgedAccounts);
      const formattedAccounts = allAccounts.map((account) => {
        formatAccountType('TransferOnDeath', 'Transfer On Death', account);
        return account;
      });
      // partition out the workplace retirement accounts
      const [standardAccounts, workplaceAccounts] = partition(
        formattedAccounts,
        (a) => !isWorkplaceRetirement(a.productType)
      );
      const filteredWorkplaceAccounts = filter(
        workplaceAccounts,
        (a) => !a.purgedAccount
      );

      const has529Only = every(standardAccounts, (a) =>
        isCollegeSavings(a.productType)
      );
      const has529Accounts = some(standardAccounts, (a) =>
        isCollegeSavings(a.productType)
      );

      Store.dispatch(receivedAccounts(standardAccounts, workplaceAccounts));

      // custom groups used for filtering on account details
      getAccountGroups();

      // accounts must be received first before retrieving performance
      const performanceAccountsById = keyBy(standardAccounts, 'accountId');
      getAccountPerformance(performanceAccountsById);

      if (!has529Only && filteredWorkplaceAccounts.length && !isInternal) {
        //Workplace YTD Balances
        getWorkplaceYearEndBalanceData(filteredWorkplaceAccounts);
      }

      // mmr messages
      getMmrMessages();
      // pending transactions required for message on balance bar
      getPendingTransactions();
      // gift indicators
      if (has529Accounts && !isInternal) {
        // this will eventually move to card data provider
        getGiftingProfileIndicators(standardAccounts);
      }
      // confidence scores
      getConfidenceScores();
      // NASU applications
      nasuInprogressReturnJourneyTile()
        ? getInProgressApplicationsV2()
        : getInProgressApplications();
      getPriorBusinessDay();
    })
    .catch((error) => {
      console.log(error);
      if (hasCustomError(error, 'EXCEEDS_ACCOUNTS_MAX')) {
        Store.dispatch(accountRestriction(error));
      } else {
        Store.dispatch(errorAccounts(error));
      }
    });
};

export const getAccountBalances = (isInternal) => {
  Store.dispatch(requestedPurgedAccounts());
  Store.dispatch(requestedAccounts());
  let purgedAccounts = [];
  return getPurgedAccounts()
    .then((purged) => {
      purgedAccounts = purged;
      Store.dispatch(receivedPurgedAccounts(purged));
      getAccounts(purgedAccounts, isInternal);
    })
    .catch((err) => {
      Store.dispatch(errorPurgedAccounts(err));
      // no matter what we need to try to get accounts, but we are doing it in the callback of purged to make
      // sure that purged accounts are in included in performance
      getAccounts(purgedAccounts, isInternal);
    });
};

export const getYTDBalanceData = (begin, end, accounts) => {
  Store.dispatch(requestedYTDBalanceData(begin, end));
  const balanceAccounts = accounts.filter((a) => {
    if (a && a.accountId) {
      return a;
    }
  });
  return aamFetch(
    'api/accounts/activities/aggregate',
    postOptionsWithBody({
      beginDate: yyyymmdd(begin),
      endDate: yyyymmdd(end),
      accounts: arrayOfAccountDescriptors(balanceAccounts)
    })
  )
    .then((history) => Store.dispatch(receivedYTDBalanceData(history)))
    .catch((error) => Store.dispatch(errorYTDBalanceData(error)));
};

export const getWorkplaceYearEndBalanceData = (accounts) => {
  Store.dispatch(requestedWorkplaceYearEndBalanceData());
  const planNumbers = [];
  accounts.forEach(
    (acct) => acct.rpsPlanNumber && planNumbers.push(acct.rpsPlanNumber)
  );
  if (planNumbers.length) {
    const body = {
      planNumbers: uniq(planNumbers)
    };
    aamFetch('api/retirement/partic-account-growth', postOptionsWithBody(body))
      .then((response) =>
        Store.dispatch(receivedWorkplaceYearEndBalanceData(response))
      )
      .catch((error) =>
        Store.dispatch(errorWorkplaceYearEndBalanceData(error))
      );
  }
};

export const getAccountActivity = (fromDate, toDate, accounts) => {
  const descriptors = arrayOfAccountDescriptors(accounts);
  Store.dispatch(requestedHistory(fromDate, toDate));

  const body = {
    beginDate: yyyymmdd(fromDate),
    endDate: yyyymmdd(toDate),
    accounts: descriptors
  };

  return aamFetch(
    'api/accounts/activities/aggregate',
    postOptionsWithBody(body)
  )
    .then((history) => Store.dispatch(receivedHistory(history)))
    .catch((error) => Store.dispatch(errorHistory(error, fromDate, toDate)));
};

export const getIntradayPricing = (isCascadingIntraday = false, isAccountSpecific = true) => {
  if (isCascadingIntraday) {
    Store.dispatch(requestedIntradayPricingAutomatic());
  } else {
    Store.dispatch(requestedIntradayPricingManual());
  }
  aamFetch(
    'api/accounts/brokerage/balances/intraday',
    getOptions(),
    BYPASS_CACHE
  )
    .then((response) => {
      if (isCascadingIntraday) {
        Store.dispatch(receivedIntradayPricingAutomatic(response));
      } else {
        if (isCascadingIntraday) {
          if (hasIntradayFail(response)) {
            Store.dispatch(errorIntradayPricingAutomatic(''));
          } else {
            Store.dispatch(receivedIntradayPricingAutomatic(response));
          }
        } else {
          if (!isAccountSpecific && hasIntradayFail(response)) {
            Store.dispatch(errorIntradayPricingManual(''));
          } else {
            Store.dispatch(receivedIntradayPricingManual(response));
          }
        }
      }
    })
    .catch((error) => {
      if (isCascadingIntraday) {
        Store.dispatch(errorIntradayPricingAutomatic(error));
      } else {
        Store.dispatch(errorIntradayPricingManual(error));
      }
    });
};

export const getCDSPortfolio = () => {
  Store.dispatch(requestedCDSPortfolio());

  aamFetch('api/taxonomy-portfolio', getOptions(), BYPASS_CACHE)
    .then((response) => {
      Store.dispatch(receivedCDSPortfolio(response));
    })
    .catch((error) => {
      Store.dispatch(errorCDSPortfolio(error));
    });
};

export const getUnrealizedGainsLosses = () => {
    Store.dispatch(requestedUnrealizedGainsLosses());

    aamFetch(
        'api/accounts/brokerage/unrealized-gains-losses',
        getOptions(),
        BYPASS_CACHE)
        .then((response) => {
            Store.dispatch(receivedUnrealizedGainsLosses(response));
        })
        .catch((error) => {
            Store.dispatch(errorUnrealizedGainsLosses(error));
        });
};

export const getRealizedGainsLossesCurrent = () => {
  Store.dispatch(requestedRealizedGainsLossesCurrent());

  aamFetch(
      'api/accounts/brokerage/realized-gains-losses/current',
      getOptions(),
      BYPASS_CACHE)
      .then((response) => {
        Store.dispatch(receivedRealizedGainsLossesCurrent(response));
      })
      .catch((error) => {
        Store.dispatch(errorRealizedGainsLossesCurrent(error));
      });
};

export const getRealizedGainsLossesPrior = () => {
  Store.dispatch(requestedRealizedGainsLossesPrior());

  aamFetch(
      'api/accounts/brokerage/realized-gains-losses/prior',
      getOptions(),
      BYPASS_CACHE)
      .then((response) => {
        Store.dispatch(receivedRealizedGainsLossesPrior(response));
      })
      .catch((error) => {
        Store.dispatch(errorRealizedGainsLossesPrior(error));
      });
};

export const getBrokerageAssertion = (agreementId) => {
  const body = {
    selectedAgreementId: agreementId,
    tradingAction: 'Buy'
  };
  Store.dispatch(requestedBrokerageAssertion());
  aamFetch(
    `${GLOBALS.AAM_TRP_BASE_APIGEE_URL}/investcloud-service/api/sso/saml/trading`,
    postOptionsWithBody(body),
    BYPASS_CACHE
  )
    .then((response) => Store.dispatch(receivedBrokerageAssertion(response)))
    .catch((error) => Store.dispatch(errorRetrievingBrokerageAssertion(error)));
};

export const getPershingLogout = () => {
  return aamFetch(NET_X_LOGOUT_URL, { method: 'GET' })
    .then(() => {})
    .catch(() => {});
};

export const getPershingSSOLogout = () => {
  return aamFetch(NET_X_MESSAGE_URL, { method: 'GET' })
    .then(() => {})
    .catch(() => {});
};

export const resetAccountEligibleForExchange = () => {
  Store.dispatch(receivedAvailableFunds([]))
  Store.dispatch(requestedAvailableFunds());
}
export const getAccountsEligibleForExchange = (account) => {
  const accountDescriptor = getAccountDescriptor(account);
  Store.dispatch(requestedAvailableFunds());
  return aamFetch(
    'api/accounts/exchange/eligible',
    postOptionsWithBody(accountDescriptor)
  )
    .then((availableFunds) =>
      Store.dispatch(receivedAvailableFunds(availableFunds))
    )
    .catch((error) => Store.dispatch(errorAvailableFunds(error)));
};

export const getAccountHistory = (account, range) => {
  const { accountChecksum, accountId, accountNumber, productType } = account;

  const beginDate = range.fromDate;
  const endDate = range.toDate;

  Store.dispatch(requestedAccountHistory(beginDate, endDate));

  const body = {
    accounts: [
      {
        accountChecksum,
        accountId,
        accountNumber,
        productType
      }
    ],
    beginDate,
    endDate
  };

  return aamFetch(
    'api/accounts/activities/aggregate',
    postOptionsWithBody(body)
  )
    .then((history) => Store.dispatch(receivedAccountHistory(history)))
    .catch((error) =>
      Store.dispatch(errorAccountHistory(error, beginDate, endDate))
    );
};

export const getAccountGroups = () => {
  Store.dispatch(requestedCustomAccountGroups());
  aamFetch('api/accounts/groups', getOptions(), BYPASS_CACHE)
    .then((customAccountGroup) =>
      Store.dispatch(receivedCustomAccountGroups(customAccountGroup))
    )
    .catch((error) => Store.dispatch(errorCustomAccountGroups(error)));
};

export const unregisterUserData = () => {
  return aamFetch(
    'api/accounts/external/unregister',
    postOptionsWithBody(),
    BYPASS_CACHE
  )
    .then(noop)
    .catch(noop);
};

export const updateExternalInvestmentConsent = (
  consented,
  accounts,
  consentStatus
) => {
  Store.dispatch(requestedSetExternalInvestmentConsent());
  const payload = {
    updateFlag: consented,
    accountDescriptors: arrayOfAccountDescriptors(accounts),
    status: consentStatus
  };
  return aamFetch(
    'api/accounts/external/consent',
    postOptionsWithBody(payload),
    BYPASS_CACHE
  )
    .then(() => {
      Store.dispatch(receivedSetExternalInvestmentConsent(consented));
      Store.dispatch(
        updateExternalInvestmentStatus({ status: 'UPDATED_CONSENT' })
      );
      if (!consented) {
        unregisterUserData();
      }
    })
    .catch((error) => {
      Store.dispatch(errorSetExternalInvestmentConsent(error));
      throw new Error(error);
    });
};

export const getYodleeAccountCenterForm = () => {
  aamFetch('api/accounts/external/account-center-form', getOptions())
    .then((accountCenterIframe) => accountCenterIframe)
    .catch((error) => error);
};

export const getConfidenceScores = () => {
  Store.dispatch(requestedConfidenceScores());
  aamFetch('api/accounts/confidencescores', getOptions())
    .then((confidenceScores) =>
      Store.dispatch(receivedConfidenceScores(confidenceScores))
    )
    .catch((error) => Store.dispatch(errorConfidenceScores(error)));
};

export const getClientMissions = () => {
  Store.dispatch(requestedMissions());
  aamFetch(
    // eslint-disable-next-line no-undef
    process.env.REACT_APP_MOCK === 'true'
      ? 'api/accounts/tracker'
      : `${GLOBALS.AAM_TRP_BASE_APIGEE_URL}/agreement-service/api/agreements/onboarding/tracker`,
    getOptions(),
    BYPASS_CACHE
  )
    .then((response) => Store.dispatch(receivedMissions(response)))
    .catch((error) => Store.dispatch(errorMissions(error)));
};

export const getClientMissionsByAgreement = (account) => {
  const payload = {
    agreementKey: {
      '@Type': 'AgreementKey',
      agreementID: account.accountId,
      agreementNumber: account.accountNumber
    }
  };
  Store.dispatch(requestedMissions());
  aamFetch(
    // eslint-disable-next-line no-undef
    process.env.REACT_APP_MOCK === 'true'
      ? 'api/accounts/tracker'
      : `${GLOBALS.AAM_TRP_BASE_APIGEE_URL}/agreement-service/api/agreements/onboarding/tracker/search`,
    postOptionsWithBody(payload)
  )
    .then((response) => Store.dispatch(receivedMissions([response])))
    .catch((error) => Store.dispatch(errorMissions(error)));
};

export const optOutOfClientMissions = (agreements, action) => {
  if (agreements && agreements.length) {
    const actions = agreements.map((agr) => optOutOfClientMission(agr, action));
    return Promise.all(actions);
  }
};

export const optOutOfClientMission = (
  agreementKey,
  action,
  successCallback = () => {}
) => {
  Store.dispatch(
    requestedUpdateMission({
      agreementKey,
      action,
      status: 'OPT_OUT'
    })
  );

  const body = {
    agreementKey,
    actions: [action],
    status: 'OPT_OUT'
  };
  return aamFetch(
    // eslint-disable-next-line no-undef
    process.env.REACT_APP_MOCK === 'true'
      ? 'api/accounts/tracker/status'
      : `${GLOBALS.AAM_TRP_BASE_APIGEE_URL}/agreement-service/api/agreements/onboarding/tracker/status`,
    putOptionsWithBody(body),
    BYPASS_CACHE
  )
    .then((response) => {
      Store.dispatch(receivedUpdateMission(response));
      successCallback();
      return response;
    })
    .catch((error) => {
      Store.dispatch(errorUpdatingMission(error));
      throw new Error(error);
    });
};

export const getMonteCarloConfidenceScores = (body, version = 'current') => {
  Store.dispatch(requestedMonteCarloConfidenceScores());
  const api = `api/accounts/performance/confidence/${version}`; //current or recommended

  return aamFetch(api, postOptionsWithBody(body))
    .then((monteCarloConfidenceScores) =>
      Store.dispatch(
        receivedMonteCarloConfidenceScores(monteCarloConfidenceScores)
      )
    )
    .catch((error) => Store.dispatch(errorMonteCarloConfidenceScores(error)));
};

export const getDocumentReferences = (accounts, selectedYear) => {
  Store.dispatch(requestedDocumentReferences());
  // need to remove profit sharing because it doesn't have necessary IV data
  const statementEligible = accounts.filter(
    (a) => a.accountTypeId != 20 && a.accountTypeId != 21
  );
  const accountDescriptors = statementEligible.map((account) =>
    getAccountDescriptor(account)
  );

  const body = {
    accountDescriptors,
    year: selectedYear
  };

  return aamFetch('api/documents/references', postOptionsWithBody(body))
    .then((references) =>
      Store.dispatch(receivedDocumentReferences(references))
    )
    .catch((error) => Store.dispatch(errorDocumentReferences(error)));
};

export const getCCPADocumentReferences = () => {
  return aamFetch('api/documents/ccpa/references', getOptions())
    .then((references) =>
      Store.dispatch(receivedCCPADocumentReferences(references))
    )
    .catch((error) => Store.dispatch(errorCCPADocumentReferences(error)));
};

const getPDF = (
  url,
  descriptor,
  documentId,
  documentName,
  operation = 'OPEN'
) =>
  fetch(url, postOptionsWithBody(descriptor))
    .then((response) => response.blob())
    .then((pdfBlob) => {
      Store.dispatch(receivedDocumentDownload(documentId));
      const file = new Blob([pdfBlob], { type: 'application/pdf' });
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(file, `${documentName}.pdf`);
      } else {
        if (operation === 'OPEN') {
          const fileUrl = URL.createObjectURL(file);
          const newWindow = window.open();
          setTimeout(() => {
            if (newWindow) {
              newWindow.document.title = `${documentName}`;
            }
          }, 500);

          if (newWindow) {
            newWindow.document.write(`
            <style> body { margin: 0 } </style>
            <iframe src="${fileUrl}"
                    width="100%"
                    height="100%"/>
          `);
          }
        } else {
          const fileUrl = URL.createObjectURL(file);
          var downloadAnchor = document.createElement('a');
          downloadAnchor.href = fileUrl;
          downloadAnchor.download = `${documentName}.pdf`;
          document.body.appendChild(downloadAnchor);
          downloadAnchor.click();
          downloadAnchor.remove();
        }
      }
    })
    .catch((error) => {
      Store.dispatch(errorDocumentDownload(error, documentId));
    });

export const getDocumentAndOpen = (
  accounts,
  accountNumber,
  accountChecksum,
  documentId,
  documentName,
  productType,
  documentType
) => {
  Store.dispatch(requestedDocumentDownload(documentId));
  const descriptor = {
    documentId,
    documentType,
    documentName,
    productType
  };

  if (
    isActiveAdvisory(productType) ||
    (isBrokerage(productType) &&
      !isNil(documentType) &&
      documentType !== BROKERAGE_CONFIRMATION) ||
    isActivePlus(productType)
  ) {
    const account = accounts.find(
      ({ accountChecksum: checkSum, accountNumber: acctNumber }) =>
        checkSum === accountChecksum && acctNumber === accountNumber
    );

    if (documentAccountShellEnabled()) {
      if (account) {
        const accountDescriptor = getAccountDescriptor(account);
        descriptor.accountDescriptors = [accountDescriptor];
      } else {
        const shellAccountDescriptor = {
          accountNumber,
          accountChecksum,
          productType
        };
        descriptor.accountDescriptors = [shellAccountDescriptor];
      }
    } else {
      const accountDescriptor = getAccountDescriptor(account);
      descriptor.accountDescriptors = [accountDescriptor];
    }
  }

  return documentType === CCPA_DOCUMENT_ID
    ? getPDF(
        `${appContext}api/documents/ccpa/pdf`,
        { referenceId: documentId },
        documentId,
        documentName,
        'OPEN'
      )
    : getPDF(
        `${appContext}api/documents/pdf`,
        descriptor,
        documentId,
        documentName,
        'OPEN'
      );
};

export const getDocumentAndDownload = (
  accounts,
  accountNumber,
  accountChecksum,
  documentId,
  documentName,
  productType,
  documentType
) => {
  Store.dispatch(requestedDocumentDownload(documentId));
  const descriptor = {
    documentId,
    documentType,
    documentName,
    productType
  };

  if (
    isActiveAdvisory(productType) ||
    (isBrokerage(productType) &&
      !isNil(documentType) &&
      documentType !== BROKERAGE_CONFIRMATION) ||
    isActivePlus(productType)
  ) {
    const account = accounts.find(
      ({ accountChecksum: checkSum, accountNumber: acctNumber }) =>
        checkSum === accountChecksum && acctNumber === accountNumber
    );
    if (documentAccountShellEnabled()) {
      if (account) {
        const accountDescriptor = getAccountDescriptor(account);
        descriptor.accountDescriptors = [accountDescriptor];
      } else {
        const shellAccountDescriptor = {
          accountNumber,
          accountChecksum,
          productType
        };
        descriptor.accountDescriptors = [shellAccountDescriptor];
      }
    } else {
      const accountDescriptor = getAccountDescriptor(account);
      descriptor.accountDescriptors = [accountDescriptor];
    }
  }

  return documentType === CCPA_DOCUMENT_ID
    ? getPDF(
        `${appContext}api/documents/ccpa/pdf`,
        { referenceId: documentId },
        documentId,
        documentName,
        'DOWNLOAD'
      )
    : getPDF(
        `${appContext}api/documents/pdf`,
        descriptor,
        documentId,
        documentName,
        'DOWNLOAD'
      );
};

export const getGiftingProfileIndicators = (accounts) => {
  Store.dispatch(requestedGiftIndicators());

  const body = arrayOfAccountDescriptors(accounts).filter(({ productType }) =>
    isCollegeSavings(productType)
  );

  return aamFetch('api/accounts/giftingindicators', postOptionsWithBody(body))
    .then((indicators) => Store.dispatch(receivedGiftIndicators(indicators)))
    .catch((error) => Store.dispatch(errorGiftIndicators(error)));
};

export const getTaxes = () => {
  Store.dispatch(requestedTaxes());
  return aamFetch('api/accounts/taxes', getOptions(), BYPASS_CACHE)
    .then((response) => Store.dispatch(receivedTaxes(response)))
    .catch((error) => Store.dispatch(errorTaxes(error)));
};

export const getContributionLimit = () => {
  Store.dispatch(requestedContributionLimit());

  return aamFetch(
    'api/transactions/customer-ira-contribution-limits',
    getOptions()
  )
    .then((response) => Store.dispatch(receivedContributionLimit(response)))
    .catch((error) => Store.dispatch(errorContributionLimit(error)));
};

export const getContributionPeriod = (customerState) => {
  Store.dispatch(requestedContributionPeriod());
  return aamFetch(
    'api/transactions/ira-contribution-period/' + customerState,
    getOptions()
  )
    .then((response) => Store.dispatch(receivedContributionPeriod(response)))
    .catch((error) => Store.dispatch(errorContributionPeriod(error)));
};

export const getMessageNotifications = () => {
  Store.dispatch(requestedMessages());
  const fetchUrl = newMessageCenterCountEnabled()
    ? `${messageServiceBasePath()}/api/inbox-summary`
    : 'api/messages/notifications';
  aamFetch(fetchUrl, getOptions(), BYPASS_CACHE)
    .then((messages) => Store.dispatch(receivedMessages(messages)))
    .catch((error) => Store.dispatch(errorMessages(error)));
};

export const getAccountGrowth = (beginDate, endDate, filteredAccounts) => {
  const descriptors = arrayOfAccountDescriptors(filteredAccounts);
  const body = {
    beginDate,
    endDate,
    accounts: descriptors
  };

  Store.dispatch(requestedAccountGrowth(beginDate, endDate));
  return aamFetch('api/accounts/history', postOptionsWithBody(body))
    .then((growth) => Store.dispatch(receivedAccountGrowth(growth)))
    .catch((error) =>
      Store.dispatch(errorAccountGrowth(error, beginDate, endDate))
    );
};

export const getIndividualAccountGrowth = (account, range) => {
  const beginDate = range.fromDate;
  const endDate = range.toDate;

  const { accountId, accountNumber, accountChecksum, productType } = account;
  const accounts = [{ accountId, accountNumber, accountChecksum, productType }];

  Store.dispatch(requestedIndividualAccountGrowth(accountId));

  const body = {
    beginDate,
    endDate,
    accounts
  };

  return aamFetch('api/accounts/history', postOptionsWithBody(body))
    .then((history) => Store.dispatch(receivedIndividualAccountGrowth(history)))
    .catch((error) => Store.dispatch(errorIndividualAccountGrowth(error)));
};

const loadCommentary = async (type) => {
  return commentaryMap[type] || {};
};

export const getCommentary = (type) => {
  if (!isCommentaryLoaded(Store.getState(), type)) {
    Store.dispatch(requestedCommentary(type));
    loadCommentary(type)
      .then((commentary) => Store.dispatch(receivedCommentary(commentary)))
      .catch((error) => Store.dispatch(errorCommentary(error, type)));
  }
  return Promise.resolve();
};

const getAggregatePerformance = (
  accountIds,
  requestedAction,
  receivedAction,
  errorAction
) => {
  Store.dispatch(requestedAction());
  const accounts = arrayOfAccountDescriptors(accountIds, false);
  aamFetch('api/accounts/performance/aggregate', postOptionsWithBody(accounts))
    .then((performance) => Store.dispatch(receivedAction(performance)))
    .catch((error) => Store.dispatch(errorAction(error)));
};

/**
 * Leveraged for MF, 529 only scenario, where product performance can be aggreagated
 *
 * @param accounts
 */
export const getAggregatedProductPerformance = (accounts) => {
  getAggregatePerformance(
    accounts,
    requestedAggregatedProductPerformance,
    receivedAggregatedProductPerformance,
    errorAggregatedProductPerformance
  );
};

/**
 * Leveraged solely from the performance page so as not to impact aggregate PRR
 * displayed on other pages.
 *
 * @param accounts
 */
export const getConsolidatedPerformance = (accounts) => {
  getAggregatePerformance(
    accounts,
    requestedConsolidatedPerformance,
    receivedConsolidatedPerformance,
    errorConsolidatedPerformance
  );
};

/**
 * Leveraged solely from the dashboard AggregatePerformanceCard component.
 *
 * @param accounts
 */
export const getTotalPerformance = (accounts) => {
  getAggregatePerformance(
    accounts,
    requestedTotalPerformance,
    receivedTotalPerformance,
    errorTotalPerformance
  );
};

export const getProductPerformance = (accounts) => {
  const descriptors = arrayOfAccountDescriptors(accounts, false);
  aamFetch('api/accounts/performance/product', postOptionsWithBody(descriptors))
    .then((productPerformance) =>
      Store.dispatch(receivedProductPerformance(productPerformance))
    )
    .catch((error) => Store.dispatch(errorProductPerformance(error)));
};

export const getAccountPerformance = (accountIds) => {
  if (accountIds.length === 0) {
    // haven't received accounts yet.. we'll get triggered again when we do
    return;
  }

  Store.dispatch(requestedAccountPerformance());
  const accounts = arrayOfAccountDescriptors(accountIds, false);

  aamFetch('api/accounts/performance', postOptionsWithBody(accounts))
    .then((accountPerformance) =>
      Store.dispatch(receivedAccountPerformance(accountPerformance))
    )
    .catch((error) => Store.dispatch(errorAccountPerformance(error)));
};

export const getPriorBusinessDay = () => {
  Store.dispatch(requestedPriorBusinessDate());
  return aamFetch('api/transactions/latest-pricing-date', getOptions())
    .then((priorBusinessDay) =>
      Store.dispatch(receivedPriorBusinessDay(priorBusinessDay))
    )
    .catch((error) => Store.dispatch(errorPriorBusinessDay(error)));
};

export const getHistoricalTransactions = (numberOfDays = 180) => {
  const options = {
    numberOfDays
  };

  Store.dispatch(requestedHistoricalTransactions());
  return aamFetch('api/transactions/historical', postOptionsWithBody(options))
    .then((transactions) =>
      Store.dispatch(receivedHistoricalTransactions(transactions))
    )
    .catch((error) => Store.dispatch(errorHistoricalTransactions(error)));
};

export const getHistoricalTransactionsV2 = (numberOfDays = 180) => {
  const options = {
    numberOfDays
  };

  Store.dispatch(requestedHistoricalTransactions());
  return aamFetch('api/activity/historical-transactions', postOptionsWithBody(options))
    .then((transactions) =>
      Store.dispatch(receivedHistoricalTransactionsV2(transactions))
    )
    .catch((error) => Store.dispatch(errorHistoricalTransactions(error)));
};

export const getPendingTransactions = (
  numberOfDays = DEFAULT_TRANSACTIONS_DAYS
) => {
  const options = {
    numberOfDays
  };
  Store.dispatch(requestedPendingTransactions());
  return aamFetch(
    'api/transactions/pending',
    postOptionsWithBody(options),
    BYPASS_CACHE
  )
    .then((transactions) =>
      Store.dispatch(receivedPendingTransactions(transactions))
    )
    .catch((error) => Store.dispatch(errorPendingTransactions(error)));
};

export const getPendingTransactionsV2 = (
  numberOfDays = DEFAULT_TRANSACTIONS_DAYS
) => {
  const options = {
    numberOfDays
  };
  Store.dispatch(requestedPendingTransactions());
  return aamFetch(
    'api/activity/pending-transactions',
    postOptionsWithBody(options),
    BYPASS_CACHE
  )
    .then((transactions) =>
      Store.dispatch(receivedPendingTransactionsV2(transactions))
    )
    .catch((error) => Store.dispatch(errorPendingTransactions(error)));
};

export const sendCancelPendingTransaction = (
  confirmationNumber,
  callBack = () => {}
) => {
  Store.dispatch(requestedCancelPendingTransaction());
  aamFetch(
    'api/transactions/cancel-pending',
    postOptionsWithBody({ confirmationNumber })
  )
    .then(() => {
      Store.dispatch(receivedCancelPendingTransaction());
      callBack();
      if (clientMissionsEnabled()) {
        try {
          getClientMissions();
        } catch (e) {
          //do nothing
        }
      }
    })
    .catch((error) => Store.dispatch(errorCancelPendingTransaction(error)));
};

export const pingServer = () => {
  return aamFetch('api/ping', getOptions(), BYPASS_CACHE)
    .then((response) => Store.dispatch(receivedServerPing(response)))
    .catch((error) => Store.dispatch(errorServerPing(error)));
};

export const postExternalAuthorization = (requestBody) =>
  aamFetch('api/accounts/authtoken', postOptionsWithBody(requestBody));

export const postExternalRetailTrac = (requestBody) =>
  aamFetch('api/pcs/auth', postOptionsWithBody(requestBody));

export const ssoAuthencation = (requestBody) => {
  Store.dispatch(requestedSsoAuthentication());
  aamFetch('api/accounts/authtoken', postOptionsWithBody(requestBody))
    .then((auth) => Store.dispatch(receivedSsoAuthentication(auth)))
    .catch((error) => Store.dispatch(errorSsoAuthentication(error)));
};

export const getRequiredMinimumDistribution = () => {
  if (isRMDDirectEnabled()) {
    getRMDDirect();
  } else {
    Store.dispatch(requestedRequiredMinimumDistribution());
    //api.dev.public
    return aamFetch('api/retirement/rmd', getOptions())
      .then((rmd) => Store.dispatch(receivedRequiredMinimumDistribution(rmd)))
      .catch((error) =>
        Store.dispatch(errorRequiredMinimumDistribution(error))
      );
  }
};

export const getRMDDirect = () => {
  Store.dispatch(requestedRequiredMinimumDistributionSummary());
  const options = getOptions();
  const optionsWithConsumerHeader = appendHeader(
    options,
    'trp-request-consumer-initial',
    'ii-account-access'
  );
  const basePath =
    // eslint-disable-next-line no-undef
    process.env.REACT_APP_MOCK === 'true'
      ? 'api'
      : GLOBALS.AAM_TRP_BASE_APIGEE_URL;

  return aamFetch(
    `${basePath}/rmd-service-auto/api/rmd-summary-plus`,
    optionsWithConsumerHeader
  )
    .then((rmd) =>
      Store.dispatch(receivedRequiredMinimumDistributionSummary(rmd))
    )
    .catch((error) =>
      Store.dispatch(errorRequiredMinimumDistributionSummary(error))
    );
};

export const getHistoricalBalancesForDate = (beginDate, accountIds) => {
  Store.dispatch(requestedHistoricalBalances(beginDate));

  aamFetch(
    'api/accounts/activities/historicalbalances',
    postOptionsWithBody({
      accounts: arrayOfAccountDescriptors(accountIds),
      beginDate,
      endDate: beginDate
    })
  )
    .then((balances) => Store.dispatch(receivedHistoricalBalances(balances)))
    .catch((error) => Store.dispatch(errorHistoricalBalances(error)));
};

export const getExternalInvestmentBalances = () => {
  Store.dispatch(requestedExternalInvestmentBalances());
  aamFetch('api/accounts/external', getOptions(), BYPASS_CACHE)
    .then((balances) =>
      Store.dispatch(receivedExternalInvestmentBalances(balances))
    )
    .catch((error) => Store.dispatch(errorExternalInvestmentBalances(error)));
};

export const refreshExternalInvestmentBalances = () => {
  Store.dispatch(requestedRefreshExternalInvestmentBalances());
  setTimeout(() => {}, 2000);
  aamFetch('api/accounts/external/refresh', getOptions(), BYPASS_CACHE)
    .then((balances) =>
      Store.dispatch(receivedRefreshExternalInvestmentBalances(balances))
    )
    .catch((error) =>
      Store.dispatch(errorRefreshExternalInvestmentBalances(error))
    );
};

export const getIRTMessages = () => {
  Store.dispatch(requestedIRTMessages());
  aamFetch('api/messages/irt', getOptions(), BYPASS_CACHE)
    .then((messages) => Store.dispatch(receivedIRTMessages(messages)))
    .catch((error) => Store.dispatch(errorIRTMessages(error)));
};

export const getContactMethodMaintenance = () => {
  Store.dispatch(requestedCMMVerification());
  aamFetch('api/customer/cmm', getOptions(), BYPASS_CACHE)
    .then((messages) => Store.dispatch(receivedCMMVerification(messages)))
    .catch((error) => Store.dispatch(errorCMMVerification(error)));
};

export const getContactMethodMaintenanceV2 = () => {
  Store.dispatch(requestedCMMVerification());
  aamFetch('api/customer/cmm-v2', getOptions())
    .then((messages) => Store.dispatch(receivedCMMVerification(messages)))
    .catch((error) => Store.dispatch(errorCMMVerification(error)));
};

export const verifyContactInformation = (
  primaryMailingAddress,
  primaryEmailAddress,
  phoneNumbers
) => {
  Store.dispatch(requestedCMMUpdate());
  const verifyCMM = {
    mailingAddresses: primaryMailingAddress ? [primaryMailingAddress] : [],
    emailAddresses: primaryEmailAddress ? [primaryEmailAddress] : [],
    phoneNumbers: phoneNumbers ? phoneNumbers : []
  };
  aamFetch(
    'api/customer/cmm/verify',
    postOptionsWithBody(verifyCMM),
    BYPASS_CACHE
  )
    .then((response) => Store.dispatch(receivedCMMUpdate(response)))
    .catch((error) => Store.dispatch(errorCMMUpdate(error)));
};

export const verifyContactInformationV2 = (
  primaryMailingAddress,
  primaryEmailAddress,
  phoneNumbers,
  residentialAddress
) => {
  Store.dispatch(requestedCMMUpdate());

  const verifyCMM = {
    mailingAddresses: primaryMailingAddress ? [primaryMailingAddress] : [],
    emailAddresses: primaryEmailAddress ? [primaryEmailAddress] : [],
    phoneNumbers: phoneNumbers ? phoneNumbers : [],
    residentialAddresses: residentialAddress ? [residentialAddress] : []
  };
  aamFetch(
    'api/customer/cmm/verify-v2',
    postOptionsWithBody(verifyCMM),
    BYPASS_CACHE
  )
    .then((response) => Store.dispatch(receivedCMMUpdate(response)))
    .catch((error) => Store.dispatch(errorCMMUpdate(error)));
};

export const verifyStopMailAddress = (primaryMailingAddress) => {
  Store.dispatch(requestedCMMUpdate());
  aamFetch(
    'api/customer/cmm/stopmail',
    postOptionsWithBody(primaryMailingAddress),
    BYPASS_CACHE
  )
    .then(() => Store.dispatch(receivedCMMUpdateStopMail()))
    .catch((error) => Store.dispatch(errorCMMUpdateStopMail(error)));
};

export const getMmrMessages = () => {
  Store.dispatch(requestedMmrMessages());
  aamFetch('api/messages/mmr', getOptions())
    .then((messages) => Store.dispatch(receivedMmrMessages(messages)))
    .catch((error) => Store.dispatch(errorMmrMessages(error)));
};

export const getInProgressApplications = () => {
  const { requestStatus } = Store.getState();
  const cmm = requestStatus.cmmVerification;
  let hasPrimaryEmailAddress;

  if (cmm && cmm === REQUEST_STATES.received) {
    const cmmEmailAddress = getCMMEmailAddress(Store.getState());
    hasPrimaryEmailAddress = !!cmmEmailAddress;

    if (hasPrimaryEmailAddress) {
      aamFetch('api/inprogress', getOptions())
        .then((response) => Store.dispatch(receivedNASUApplications(response)))
        .catch((error) => Store.dispatch(errorNASUApplications(error)));
    }
  }
};

export const getInProgressApplicationsV2 = () => {
  aamFetch('api/inprogress/v2', getOptions())
    .then((response) => Store.dispatch(receivedNASUApplications(response)))
    .catch((error) => Store.dispatch(errorNASUApplications(error)));
};

export const getConversionHistory = (details) => {
  Store.dispatch(requestedConversionHistory());
  aamFetch(
    'api/transactions/conversion-history',
    postOptionsWithBody(details),
    BYPASS_CACHE
  )
    .then((response) => Store.dispatch(receivedConversionHistory(response)))
    .catch((error) => Store.dispatch(errorConversionHistory(error)));
};

export const deleteInProgressApplicationsV2 = (applicationID, accountType) => {
  //send in nasu account type analytics on delete - US532015
  sendNASUDeleteAnalytics(accountType);
  aamFetch(
    `${GLOBALS.AAM_TRP_BASE_APIGEE_URL}/nasu-svc/service/new-account-application/${applicationID}`,
    deleteOptions()
  )
    .then((response) => {
      Store.dispatch(receivedNASUDeleteApplications(response.applicationID));
    })
    .catch((error) => Store.dispatch(errorNASUDeleteApplications(error)));
};

export const suppressIRTMessage = (messageId) => {
  aamFetch(
    `api/messages/irt/${messageId}/suppress`,
    postOptions(),
    BYPASS_CACHE
  )
    .then((response) => Store.dispatch(suppressedIRTMessage(response)))
    .catch((error) => Store.dispatch(errorIRTMessages(error)));
};

export const requestStatusMapping = {
  accountActivity: getAccountActivity,
  accountGrowth: getAccountGrowth,
  accountHistory: getAccountHistory,
  accountPerformance: () => {
    const { accounts } = Store.getState();
    getAccountPerformance(keyBy(accounts, 'accountId'));
  },
  accounts: getAccountBalances,
  consolidatedPerformance: getConsolidatedPerformance,
  customer: getCustomer,
  documentReferences: getDocumentReferences,
  historicalTransactions: getHistoricalTransactions,
  individualGrowth: getIndividualAccountGrowth,
  pendingTransactions: getPendingTransactions,
  productPerformance: () => {
    const { accounts } = Store.getState();
    getProductPerformance(keyBy(accounts, 'accountId'));
  },
  aggregatedProductPerformance: getAggregatedProductPerformance,
  taxes: getTaxes
};

export const getGrantViewAccessAddUserAuth = (requestBody) => {
  Store.dispatch(requestedGrantViewAccessAddUserAuth());
  aamFetch(
    'api/view-access/authorized',
    postOptionsWithBody(requestBody),
    BYPASS_CACHE
  )
    .then((accounts) =>
      Store.dispatch(receivedGrantViewAccessAddUserAuth(accounts))
    )
    .catch((error) => Store.dispatch(errorGrantViewAccessAddUserAuth(error)));
};

export const deleteGrantViewAccessUser = (requestBody, callback = () => {}) => {
  Store.dispatch(requestDeleteGrantViewAccessUser());
  aamFetch(
    'api/view-access/relationships',
    postOptionsWithBody(requestBody),
    BYPASS_CACHE
  )
    .then(() => {
      // currently will have no "data" in callback because of 204 response
      Store.dispatch(receivedDeleteGrantViewAccessUser(requestBody));
      callback();
    })
    .catch(() => {
      Store.dispatch(deleteGrantViewAcessError());
    });
};

export const getBankProfile = () => {
  Store.dispatch(requestedBankProfile());
  aamFetch('api/accounts/bank-profile', getOptions(), BYPASS_CACHE)
    .then((bankProfile) => Store.dispatch(receivedBankProfile(bankProfile)))
    .catch((error) => Store.dispatch(errorBankProfile(error)));
};

export const getKiplingerRegistration = () => {
  Store.dispatch(requestedKiplingerRegistrationInfo());
  return aamFetch(
    'api/accounts/kiplinger/registration/response',
    getOptions(),
    BYPASS_CACHE
  )
    .then((response) =>
      Store.dispatch(receivedKiplingerRegistrationInfo(response))
    )
    .catch((error) => Store.dispatch(errorKiplingerRegistrationInfo(error)));
};

export const getMorningstarAssociation = () => {
  Store.dispatch(requestedMorningstarAssociation());
  return aamFetch('api/morningstar/association', getOptions(), BYPASS_CACHE)
    .then((response) =>
      Store.dispatch(receivedMorningstarAssociation(response))
    )
    .catch((error) => Store.dispatch(errorMorningstarAssociation(error)));
};

export const getMorningstarAssociationUrl = () => {
  Store.dispatch(requestedMorningstarAssociationUrl());
  return aamFetch('api/morningstar/association/url', getOptions(), BYPASS_CACHE)
    .then((response) =>
      Store.dispatch(receivedMorningstarAssociationUrl(response))
    )
    .catch((error) => Store.dispatch(errorMorningstarAssociationUrl(error)));
};

export const getMorningstarRegistrationCallback = (finderId) => {
  Store.dispatch(requestedMorningstarRegistrationCallback());
  return aamFetch(
    `api/morningstar/registration/${finderId}`,
    getOptions(),
    BYPASS_CACHE
  )
    .then((response) =>
      Store.dispatch(receivedMorningstarRegistrationCallback(response))
    )
    .catch((error) =>
      Store.dispatch(errorMorningstarRegistrationCallback(error))
    );
};

export const requestMorningstarConsent = () => {
  Store.dispatch(requestedMorningstarConsent());
  return aamFetch(
    'api/customer/service-type/subscribe/40',
    postOptions(),
    BYPASS_CACHE
  )
    .then((response) => Store.dispatch(receivedMorningstarConsent(response)))
    .catch((error) => Store.dispatch(errorMorningstarConsent(error)));
};

export const getHouseholdInformation = (webViewEnabled) => {
  Store.dispatch(requestedHouseholdInformation());
  return aamFetch('api/accounts/household', getOptions())
    .then((response) => {
      Store.dispatch(receivedHouseholdInformation(response));
      if (response) {
        const {
          householdBalanceTierStatus: {
            householdAssetTier: {
              kiplingerAccess = false,
              morningstarPremiumAccess = false
            }
          }
        } = response;

        if (kiplingerAccess && !webViewEnabled) {
          getKiplingerRegistration();
        }

        if (morningstarPremiumAccess) {
          getMorningstarAssociation();
        }
      }
    })
    .catch((error) => Store.dispatch(errorHouseholdInformation(error)));
};

export const updateBankAssociations = (payload, callback = () => {}) => {
  Store.dispatch(requestedUpdateBankAssociations());
  aamFetch('api/accounts/bank', putOptionsWithBody(payload), BYPASS_CACHE)
    .then(() => {
      Store.dispatch(receivedUpdateBankAssociations());
      Store.dispatch(analyticsActionSuccess('ProfPref', 'BankInvestmentsAdd'));
      callback();
    })
    .catch(() => {
      Store.dispatch(analyticsActionError('BankInvestmentsAdd'));
      Store.dispatch(errorUpdateBankAssociations());
    });
};

export const deleteBank = (
  payload,
  callback = () => {},
  deleteBank = false
) => {
  Store.dispatch(requestedDeleteBank());
  aamFetch('api/accounts/bank', deleteOptionsWithBody(payload), BYPASS_CACHE)
    .then(() => {
      Store.dispatch(receivedDeleteBank());
      if (!deleteBank) {
        Store.dispatch(
          analyticsActionSuccess('ProfPref', 'BankInvestmentsDelete')
        );
      }
      callback();
    })
    .catch(() => {
      Store.dispatch(errorDeleteBank());
      if (deleteBank) {
        Store.dispatch(analyticsActionError('BankAccountDelete'));
      } else {
        Store.dispatch(analyticsActionError('BankInvestmentsDelete'));
      }
    });
};

export const reinitiateChallengeDeposits = (payload, callback = () => {}) => {
  Store.dispatch(requestedReinitiateChallengeDeposits());
  aamFetch(
    'api/accounts/bank-reinitiate-challenge-deposits',
    postOptionsWithBody(payload),
    BYPASS_CACHE
  )
    .then(() => {
      Store.dispatch(receivedReinitiateChallengeDeposits(payload.bankAccount));
      callback();
    })
    .catch(() =>
      Store.dispatch(errorReinitiateChallengeDeposits(payload.bankAccount))
    );
};

export const challengeDepositVerification = (payload, callback = () => {}) => {
  Store.dispatch(requestedChallengeDepositVerification());
  aamFetch(
    'api/accounts/bank-validate-challenge-deposits',
    postOptionsWithBody(payload),
    BYPASS_CACHE
  )
    .then(() => {
      Store.dispatch(receivedChallengeDepositVerification(payload.bankAccount));
      callback();
    })
    .catch(() =>
      Store.dispatch(errorChallengeDepositVerification(payload.bankAccount))
    );
};

export const getBankVerifications = () => {
  aamFetch('api/accounts/bank-agreement-verifications', getOptions())
    .then(() => {})
    .catch((error) => console.log(error));
};

export const updateBankVerificationStatus = (bankAccount) => {
  return aamFetch(
    'api/accounts/bank/verification-status',
    putOptionsWithBody(bankAccount)
  );
};

export const addVerifiedBank = (bankAccount) => {
  return aamFetch(
    'api/accounts/bank/verified-bank',
    postOptionsWithBody(bankAccount)
  );
};

export const getEligibleAgreementsForBank = (payload) => {
  Store.dispatch(requestedEligibleAgreementsForBank());
  aamFetch(
    'api/accounts/eligible-banking-agreements',
    postOptionsWithBody(payload),
    BYPASS_CACHE
  )
    .then((agreements) => {
      Store.dispatch(receivedEligibleAgreementsForBank(agreements));
    })
    .catch(() => Store.dispatch(errorEligibleAgreementsForBank()));
};

export const getIsActivatedForTextAlerts = (payload) => {
  Store.dispatch(requestedActivatedForTextAlerts());
  aamFetch(
    'api/activity/activated-text-message-delivery/check',
    postOptionsWithBody(payload),
    BYPASS_CACHE
  )
    .then((response) => {
      Store.dispatch(receivedActivatedForTextAlerts(response));
    })
    .catch(() => Store.dispatch(errorActivatedForTextAlerts()));
};

export const getAlertsAndNotifications = () => {
  Store.dispatch(requestedAlertsAndNotifications());
  aamFetch('api/activity/alert-preferences', getOptions(), BYPASS_CACHE)
    .then((alertPreferences) => {
      Store.dispatch(receivedAlertsAndNotifications(alertPreferences));
    })
    .catch(() => Store.dispatch(errorAlertsAndNotifications()));
};

export const updateAlertsAndNotifications = (payload, silent = false) => {
  if (!silent) {
    Store.dispatch(requestedUpdateAlertsAndNotifications());
  }
  aamFetch(
    'api/activity/alert-preferences',
    putOptionsWithBody(payload),
    BYPASS_CACHE
  )
    .then(() => {
      if (!silent) {
        Store.dispatch(receivedUpdateAlertsAndNotifications());
      }
    })
    .catch(() => {
      if (!silent) {
        Store.dispatch(errorUpdateAlertsAndNotifications());
      }
    });
};

export const getSmsActivationCode = (payload, callback = () => {}) => {
  Store.dispatch(requestedSendSmsActivationCode());
  aamFetch('api/token/generate', postOptionsWithBody(payload), BYPASS_CACHE)
    .then(() => {
      Store.dispatch(receivedSendSmsActivationCode());
      callback();
    })
    .catch(() => Store.dispatch(errorSendSmsActivationCode()));
};

export const verifySmsActivationCode = (
  token,
  failureCallback = () => {},
  successCallback = () => {}
) => {
  Store.dispatch(requestedVerifySmsActivationCode());
  const otpToken = { token };
  aamFetch('api/token/validate', postOptionsWithBody(otpToken), BYPASS_CACHE)
    .then((tokenValidationResponse) => {
      const { result } = tokenValidationResponse;
      Store.dispatch(receivedVerifySmsActivationCode(result));
      if (result === true) {
        successCallback();
      } else {
        failureCallback();
      }
    })
    .catch(() => {
      Store.dispatch(errorVerifySmsActivationCode());
      failureCallback();
    });
};

export const submitSmsActivation = (payload, callback = () => {}) => {
  Store.dispatch(requestedSubmitSmsActivation());
  aamFetch(
    'api/activity/activate-text-message-delivery',
    postOptionsWithBody(payload)
  )
    .then(() => {
      Store.dispatch(receivedSubmitSmsActivation());
      callback();
    })
    .catch(() => Store.dispatch(errorSubmitSmsActivation()));
};

export const getSecurityOperationalConsent = () => {
  Store.dispatch(requestedSecurityOperationalConsent());
  aamFetch(
    'api/activity/has-security-operational-text-message-consent',
    getOptions(),
    BYPASS_CACHE
  )
    .then((consent) => {
      Store.dispatch(receivedSecurityOperationalConsent(consent));
    })
    .catch(() => Store.dispatch(errorSecurityOperationalConsent()));
};

export const getBucketRollData = () => {
  Store.dispatch(requestedBucketRoll());
  aamFetch('api/transactions/date/bucket-roll-period', getOptions())
    .then((data) => {
      Store.dispatch(receivedBucketRoll(data));
    })
    .catch(() => Store.dispatch(errorBucketRoll()));
};

export const lookupMobileCarrier = () => {
  Store.dispatch(requestedMobileCarriers());
  aamFetch('api/activity/mobile-carrier-lookup', getOptions())
    .then((result) => {
      Store.dispatch(receivedMobileCarriers(result));
    })
    .catch(() => Store.dispatch(errorMobileCarriers()));
};

export const getMobilePhoneShortCodeDeactivationReason = (payload) => {
  Store.dispatch(requestedShortCodeDeactivationReason());
  aamFetch(
    'api/activity/mobile-phone-short-code-deactivation-reason',
    postOptionsWithBody(payload)
  )
    .then((result) => {
      Store.dispatch(receivedShortCodeDeactivationReason(result));
    })
    .catch(() => Store.dispatch(errorShortCodeDeactivationReason()));
};

export const getPortfolio = () => {
  Store.dispatch(requestedPortfolio());
  aamFetch('api/portfolio', getOptions())
    .then((result) => {
      Store.dispatch(receivedPortfolio(result));
    })
    .catch(() => Store.dispatch(errorPortfolio()));
};

export const getPershingJWT = (loggedIn = false, ldapId) => {
  Store.dispatch(requestedBrokerageAssertionMFE());
  aamFetch('api/pershing/jwt', getOptions(), BYPASS_CACHE)
    .then((result) => {
      if (!loggedIn) {
        window.pershingMFEHelper.login(ldapId, result.jws);
      } else {
        window.pershingMFEHelper.renewAuthToken(result.jws);
      }
      Store.dispatch(receivedBrokerageMFE(result));
    })
    .catch((error) => {
      Store.dispatch(updateBrokerageMFEState(MFE_STATE.ERROR));
      Store.dispatch(errorRetrievingBrokerageMFE(error));
    });
};


export const getIceSAMLResponse = () => {
  Store.dispatch(requestedFixedIncomeAssertion());
  return aamFetch('api/brokerage/tmc', getOptions(), BYPASS_CACHE)
    .then((result) => {
      Store.dispatch(receivedFixedIncomeAssertion(result));
      return result;
    })
    .catch((error) => {
      Store.dispatch(errorRetrievingFixedIncomeAssertion(error));
    });
};

export const resetBrokerageMFE = () => {
  Store.dispatch(resetRetrievingBrokerageMFE());
}

const fetchCache = {};
const cacheAgeTracker = {};
const MAX_CACHE_LENGTH = 100;
const OLD_CACHE_ENTRIES_COUNT = 20;

export function aamFetch(input, init, bypass) {
  const bypassEnabled = bypass === BYPASS_CACHE;
  const hashKey = input + JSON.stringify(init);
  return new Promise((resolve, reject) => {
    const cached = fetchCache[hashKey];
    if (cached !== undefined) {
      cacheAgeTracker[hashKey] = Date.now();
      resolve(cached);

      if (Object.keys(fetchCache).length >= MAX_CACHE_LENGTH) {
        const oldCacheEntries = Object.values(cacheAgeTracker)
          .sort((x, y) => x - y)
          .splice(0, OLD_CACHE_ENTRIES_COUNT);
        Object.keys(cacheAgeTracker).forEach((key) => {
          const index = oldCacheEntries.indexOf(cacheAgeTracker[key]);
          if (index > -1) {
            delete fetchCache[key];
            delete cacheAgeTracker[key];
          }
        });
      }
      return;
    }

    const externalIdentifier = /^(http:|https:)/g;
    const isExternal = input.search(externalIdentifier) !== -1;
    const fetchUrl = isExternal ? `${input}` : `${appContext}${input}`;

    fetch(fetchUrl, init)
      .then((response) => {
        if (response.ok) {
          return response;
        }
        throw response;
      })
      .then((response) => {
        return (response &&
          response.url.indexOf('account-center-form') !== -1) ||
          (response && response.url.indexOf('link-account-wizard-form') !== -1)
          ? response.text()
          : !response || (response && response.status === 204)
          ? null
          : response.json();
      }) // status 204 indicates success but no response body so `.json` would throw an error.
      .then((response) => {
        if (requestCacheEnabled() && !bypassEnabled) {
          fetchCache[hashKey] = response;
          cacheAgeTracker[hashKey] = Date.now();
        }
        return response;
      })
      .then(aamFetchResolved)
      .catch(aamFetchRejected);

    function aamFetchResolved(result) {
      resolve(result);
    }

    function aamFetchRejected(reason) {
      if (reason.json && reason.body && !reason.bodyUsed) {
        reason.json().then((data) => {
          reject(data);
        });
      } else {
        reject(reason);
      }
    }
  });
}
